import React from 'react';

// helpers
import {useTheme} from '@emotion/react';

// components
import {Skeleton} from '@mui/joy';

// styles
import {
  ActionColumnHeaderItem,
  TableRowBase,
  TableHeaderItem,
  TableWrapper,
} from './Table-styles';
import {TextDefault, TextSmall} from '../../styles';

type Column =
  | {
      width: string;
      empty: false;
    }
  | {
      empty: true;
    };

type Props = {
  columns: Column[];
  rowCount: number;
};

/**
 * TableSkeleton component
 * This component renders a skeleton loader for a table
 * @param props - The component props
 * @param props.columns - The columns of the table
 * @param props.rowCount - The number of rows to render
 * @returns The skeleton table
 */
const TableSkeleton = ({columns, rowCount}: Props) => {
  const {skeleton} = useTheme();

  /**
   * Render the table header for desktop view
   * @returns The table header
   */
  const renderHeader = () => {
    return (
      <thead>
        <tr>
          {columns.map((column, index) => (
            <React.Fragment key={`skeleton_table_header_${index}`}>
              {column.empty === false ? (
                <TableHeaderItem>
                  <TextDefault>
                    <Skeleton variant="text" width={skeleton.text.width.xl} />
                  </TextDefault>
                </TableHeaderItem>
              ) : (
                <ActionColumnHeaderItem />
              )}
            </React.Fragment>
          ))}
        </tr>
      </thead>
    );
  };

  /**
   * Render the table rows for desktop view
   * @returns The table rows
   */
  const renderRows = () => {
    return (
      <tbody>
        {[...Array(rowCount)].map((_, rowIndex) => (
          <TableRowBase key={`skeleton_table_row_${rowIndex}`}>
            {columns.map((item, cellIndex) => (
              <td key={`skeleton_table_row_${rowIndex}_cell_${cellIndex}`}>
                <TextSmall>
                  {item.empty === false && (
                    <Skeleton variant="text" width={item.width} />
                  )}
                </TextSmall>
              </td>
            ))}
          </TableRowBase>
        ))}
      </tbody>
    );
  };

  return (
    <TableWrapper borderAxis="none">
      {renderHeader()}
      {renderRows()}
    </TableWrapper>
  );
};

export default TableSkeleton;
